import type {
    Observer,
    Subject
} from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import type AggregationFilter from '../store/AggregationFilter';
import AggregationItems from '../store/AggregationItems';
import HouseSearchFilters from '../store/HouseSearchFilters';
import GTM from '../../../gtm/GTM';
import GTMOverviewFilterAddModel from '../../../gtm/models/GTMOverviewFilterAddModel';
import GTMOverviewFilterRemoveModel from '../../../gtm/models/GTMOverviewFilterRemoveModel';

interface AggregationComponentBindingInterface {
    aggregations: SearchResponseAggregation[];
    selectedFilters: string;
}

interface AggregationComponentContextInterface extends ContextInterface {
    element: HTMLElement;
    bindings: AggregationComponentBindingInterface;
}

export default class AggregationComponent implements Observer {
    context: AggregationComponentContextInterface;

    static getClassName(): string {
        return 'AggregationComponent';
    }

    constructor(context: AggregationComponentContextInterface) {
        this.context = context;
        this.context.bindings.aggregations = [];
    }

    update(subject: Subject): void {
        if (!(subject instanceof AggregationItems)) {
            return;
        }

        this.#updateAggregateCounters(subject);
    }

    public onInit(): void {
        this.setEventListeners();
    }

    private setEventListeners(): void {
        const checkboxes: HTMLInputElement[] = Array.from(
            this.context.element.querySelectorAll('input')
        );

        const filters = HouseSearchFilters.getInstance();
        checkboxes.forEach((input) => {
            input.addEventListener('change', async () => {
                const filter = filters.get(input.name) as AggregationFilter;

                if (input.checked) {
                    filter.add(input.value);
                    this.#trackFilterAdd(input);
                    return;
                }

                filter.remove(input.value);
                this.#trackFilterRemove(input);
            });
        });

        const aggregationItems = AggregationItems.getInstance();
        aggregationItems.attach(this);
    }

    #trackFilterAdd(input: HTMLInputElement): void {
        GTM.track(
            GTMOverviewFilterAddModel.parseToGTMData({
                label: input.value,
                category: input.name
            })
        );
    }

    #trackFilterRemove(input: HTMLInputElement): void {
        GTM.track(
            GTMOverviewFilterRemoveModel.parseToGTMData({
                label: input.value,
                category: input.name
            })
        );
    }

    #updateAggregateCounters(subject: AggregationItems): void {
        const update = (aggregationName: string, itemName: string, count: number): void => {
            const field: HTMLInputElement | null = this.context.element.querySelector(
                `[name="${aggregationName}"][value="${itemName}"]`
            );

            if (!field) {
                return;
            }

            const countElement: HTMLElement | null | undefined =
                field.nextElementSibling?.querySelector('[data-name="facet-count"]');

            if (!countElement) {
                return;
            }

            countElement.innerText = count.toString();
        };

        subject.aggregations.forEach((aggregation) => {
            aggregation.items.forEach((item) => {
                update(aggregation.name, item.name, item.count);
            });
        });
    }
}
